// Advanced Settings tab — compliance mode, payment gate, SLA, nsofar sync, status.
// Tracks dirty fields; Save Changes calls the relevant PUT endpoints.

import { useState } from "react";
import type { JSX, FormEvent } from "react";
import { toast } from "sonner";
import type { TenantConfig } from "../lib/adminApiClient.js";
import { updateNsofarSync } from "../lib/adminApiClient.js";
import { apiFetch } from "../lib/api.js";

const SA = "/api/super-admin";

interface Props { tenantId: string; config: TenantConfig; }

interface FormState {
  compliance_mode:      "standard" | "nsofar_compliance";
  payment_gate_enabled: boolean;
  report_sla_enforced:  boolean;
  status:               string;
  nsofar_sync:          boolean;
}

const INPUT = "rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="flex items-center">{children}</div>
    </div>
  );
}

function ToggleSwitch({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }): JSX.Element {
  return (
    <button
      role="switch" aria-checked={checked} disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 disabled:opacity-50 ${
        checked ? "bg-indigo-600" : "bg-slate-300"
      }`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-4" : "translate-x-0"
      }`} />
    </button>
  );
}

export function AdvancedTab({ tenantId, config }: Props): JSX.Element {
  const [form, setForm] = useState<FormState>({
    compliance_mode:      config.compliance_mode,
    payment_gate_enabled: config.payment_gate_enabled,
    report_sla_enforced:  config.report_sla_enforced,
    status:               "active",
    nsofar_sync:          false,
  });
  const [saving, setSaving] = useState(false);

  function patch<K extends keyof FormState>(key: K, val: FormState[K]): void {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function save(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSaving(true);
    try {
      await Promise.all([
        apiFetch(`${SA}/tenants/${tenantId}/status`, {
          method: "PUT", body: JSON.stringify({ status: form.status }),
        }),
        updateNsofarSync(tenantId, form.nsofar_sync),
      ]);
      toast.success("Advanced settings saved.");
    } catch {
      toast.error("Failed to save some settings.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void save(e)} className="space-y-1">
      <div className="bg-white rounded-xl border border-slate-200 px-4 divide-y divide-slate-100">
        <Field label="Compliance Mode">
          <select value={form.compliance_mode} onChange={(e) => patch("compliance_mode", e.target.value as FormState["compliance_mode"])} className={INPUT}>
            <option value="standard">Standard</option>
            <option value="nsofar_compliance">Nsofar Compliance</option>
          </select>
        </Field>
        <Field label="Payment Gate">
          <ToggleSwitch checked={form.payment_gate_enabled} onChange={(v) => patch("payment_gate_enabled", v)} />
        </Field>
        <Field label="Report SLA Enforced">
          <ToggleSwitch checked={form.report_sla_enforced} onChange={(v) => patch("report_sla_enforced", v)} />
        </Field>
        <Field label="Nsofar Sync">
          <ToggleSwitch checked={form.nsofar_sync} onChange={(v) => patch("nsofar_sync", v)} />
        </Field>
        <Field label="Tenant Status">
          <select value={form.status} onChange={(e) => patch("status", e.target.value)} className={INPUT}>
            {["active", "trial", "suspended", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>
      <div className="flex justify-end pt-3">
        <button type="submit" disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50">
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </form>
  );
}
